<div class="qa-page">
  <!-- FORCED-FAILURE TOGGLE, defined once and stamped into each step's section
       below (EDUS-2855). Six of these exist and the only thing that differs is
       which endpoint they name and which screen that produces, so a template
       beats six near-identical copies drifting apart.

       `where` is the instruction echoed back in the confirmation — a lever whose
       operator does not know what to go and look at is not much of a lever. -->
  <ng-template #failToggle let-endpoint="endpoint" let-where="where">
    <div class="qa-actions">
      <lib-cdw-button
        level="primary"
        type="button"
        [disabled]="endpointFailBusy !== null"
        (buttonClick)="setEndpointFail(endpoint, true, where)"
        [attr.data-testid]="'qa-endpoint-fail-enable-' + endpoint"
      >
        {{ endpointFailBusy === endpoint ? 'Working…' : 'Force failure' }}
      </lib-cdw-button>
      <lib-cdw-button
        level="secondary"
        type="button"
        [disabled]="endpointFailBusy !== null"
        (buttonClick)="setEndpointFail(endpoint, false, where)"
        [attr.data-testid]="'qa-endpoint-fail-clear-' + endpoint"
      >
        Clear
      </lib-cdw-button>
      <span
        *ngIf="isEndpointFailArmed(endpoint)"
        class="qa-armed"
        [attr.data-testid]="'qa-endpoint-fail-armed-' + endpoint"
      >
        Armed
      </span>
    </div>
    <p
      *ngIf="endpointFailMessage(endpoint)"
      class="qa-message"
      [attr.data-testid]="'qa-endpoint-fail-message-' + endpoint"
    >
      {{ endpointFailMessage(endpoint) }}
    </p>
    <p
      *ngIf="endpointFailError(endpoint)"
      class="qa-error"
      [attr.data-testid]="'qa-endpoint-fail-error-' + endpoint"
    >
      {{ endpointFailError(endpoint) }}
    </p>
  </ng-template>

  <h1 class="heading-lg">QA Overrides</h1>
  <p class="subtitle">
    Internal test-only actions, restricted to the fixed internal test customers.
    Pick a test domain, then use the sections below — they run in the order the
    wizard does.
  </p>

  <!-- PINNED CONTROLS. The domain selector decides what every section below
       acts on, and the banner says what that tenant is currently doing; both
       stop being useful the moment they scroll away, and the page is long
       enough now that they do. Pinned together because they are one thought:
       "this domain, in this state". -->
  <div class="qa-sticky">
    <label class="qa-field">
      <span class="qa-label">Test domain</span>
      <select
        [(ngModel)]="domain"
        (ngModelChange)="onDomainChange()"
        name="domain"
        data-testid="qa-domain"
      >
        <option *ngFor="let d of domains" [value]="d">{{ d }}</option>
      </select>
    </label>

    <!-- ACTIVE-OVERRIDE BANNER.
         Renders ONLY when something is live — that is the whole point. An
         always-present "current state" card becomes furniture within a day and
         gets skipped exactly when it matters; a banner that is usually absent
         cannot be tuned out. Carries its own Clear-all so acting on it is one
         click from the warning.

         This matters because a forgotten lever does not look like a lever: a
         stale verdict override is indistinguishable from a real block, and a
         stale sync-fail override looks like a genuine outage. -->
    <section
      *ngIf="hasActiveOverrides && state as active"
      class="qa-banner"
      role="alert"
      data-testid="qa-active-banner"
    >
      <div class="qa-banner-head">
        <span class="qa-banner-icon" aria-hidden="true">&#9888;</span>
        <h2 class="qa-banner-title">
          {{ activeOverrideCount }}
          {{ activeOverrideCount === 1 ? 'QA override is' : 'QA overrides are' }}
          active for {{ active.domain }}
        </h2>
      </div>
      <p class="qa-banner-note">
        This tenant is not behaving normally. Each lever self-heals after {{ ttlLabel }}.
      </p>

      <ul class="qa-banner-list" data-testid="qa-state-list">
        <li *ngIf="active.loginVerdict">
          <strong>Login verdict</strong> forced to
          {{ blockVerdictLabel[active.loginVerdict] }}
          <code>({{ active.loginVerdict }})</code>
        </li>
        <li *ngIf="active.licenseScenario">
          <strong>License</strong> seeded as
          <code>{{ active.licenseScenario }}</code>
        </li>
        <li *ngIf="active.inviteReject">
          <strong>Step 5 invite rejection</strong> armed as
          <code>{{ active.inviteReject }}</code> — the send will return 200 with
          failures and nothing will actually be emailed
        </li>
        <li *ngIf="active.spinnerDelayMs !== null">
          <strong>Spinner delay</strong> of
          <code>{{ active.spinnerDelayMs }}ms</code> on every onboarding request
        </li>
        <li *ngIf="active.clientIdFail">
          <strong>Step-3 load error</strong> forced — the DWD client-id fetch
          will fail
        </li>
        <li *ngFor="let endpoint of active.endpointFail">
          <strong>Forced failure</strong> — {{ endpointLabel[endpoint] }}
        </li>
        <li *ngIf="active.dwdStatus">
          <strong>DWD verify status</strong> forced to
          {{ dwdLabel(active.dwdStatus) }} <code>({{ active.dwdStatus }})</code>
        </li>
        <li *ngIf="active.syncKickoffFail">
          <strong>Sync kickoff</strong> forced to fail — Step 4 will show "Sync
          Could Not Start"
        </li>
        <li *ngIf="active.syncOutcome">
          <strong>Next sync outcome</strong> armed as
          <code>{{ active.syncOutcome }}</code> — the next kickoff will skip the
          real sync and report this immediately
        </li>
      </ul>

      <div class="qa-actions">
        <lib-cdw-button
          level="primary"
          type="button"
          [disabled]="clearAllBusy"
          (buttonClick)="clearAll()"
          data-testid="qa-clear-all"
        >
          {{ clearAllBusy ? 'Working…' : 'Clear all overrides' }}
        </lib-cdw-button>
        <lib-cdw-button
          level="secondary"
          type="button"
          [disabled]="stateBusy"
          (buttonClick)="refreshState()"
          data-testid="qa-state-refresh"
        >
          Refresh
        </lib-cdw-button>
      </div>

      <p
        *ngIf="clearAllMessage"
        class="qa-message"
        data-testid="qa-clear-all-message"
      >
        {{ clearAllMessage }}
      </p>
      <p *ngIf="clearAllError" class="qa-error" data-testid="qa-clear-all-error">
        {{ clearAllError }}
      </p>
    </section>

    <!-- The reassuring case stays deliberately quiet: a clean domain needs one
         line, not a card competing with the banner above. -->
    <p
      *ngIf="state && !hasActiveOverrides && !stateBusy"
      class="qa-state-none"
      data-testid="qa-state-none"
    >
      No overrides are active for {{ state.domain }} — it behaves normally.
      <ng-container *ngIf="state.customerId === null">
        No tenant exists for this domain yet, so the customer-scoped levers
        can't apply.
      </ng-container>
    </p>
    <p *ngIf="stateError" class="qa-error" data-testid="qa-state-error">
      {{ stateError }}
    </p>
  </div>

  <!-- Global spinner delay (EDUS-2855). First, and outside the step ordering
       below, because it is the one lever that is not about any single step. -->
  <section class="qa-section">
    <h2 class="heading-md">Spinner delay (all onboarding requests)</h2>
    <p class="subtitle">
      Adds a fixed delay to <strong>every</strong> onboarding request for the
      selected test customer, so the wizard's loading states can actually be
      looked at — on a test tenant they are otherwise gone in well under a
      second. One value covers all of them: the shell, each step's load, Verify,
      "Sync Data" and the invite send.
    </p>
    <p class="subtitle">
      Capped at {{ maxSpinnerDelayMs }}ms. Past that the request starts fighting
      HTTP timeouts, and you get a network error instead of the loading state
      you set out to see. Set 0, or use Clear, to switch it off. Expires in {{ ttlLabel }}.
    </p>

    <div class="qa-form">
      <label class="qa-field">
        <span class="qa-label">Delay per request (ms)</span>
        <input
          type="number"
          min="0"
          [max]="maxSpinnerDelayMs"
          step="500"
          [(ngModel)]="spinnerDelayMs"
          name="spinnerDelayMs"
          data-testid="qa-spinner-delay-input"
        />
      </label>

      <div class="qa-actions">
        <lib-cdw-button
          level="primary"
          type="button"
          [disabled]="spinnerDelayBusy"
          (buttonClick)="setSpinnerDelay(spinnerDelayMs)"
          data-testid="qa-spinner-delay-apply"
        >
          {{ spinnerDelayBusy ? 'Working…' : 'Set delay' }}
        </lib-cdw-button>
        <lib-cdw-button
          level="secondary"
          type="button"
          [disabled]="spinnerDelayBusy"
          (buttonClick)="setSpinnerDelay(0)"
          data-testid="qa-spinner-delay-clear"
        >
          Clear
        </lib-cdw-button>
      </div>
    </div>

    <p
      *ngIf="spinnerDelayMessage"
      class="qa-message"
      data-testid="qa-spinner-delay-message"
    >
      {{ spinnerDelayMessage }}
    </p>
    <p
      *ngIf="spinnerDelayError"
      class="qa-error"
      data-testid="qa-spinner-delay-error"
    >
      {{ spinnerDelayError }}
    </p>
  </section>

  <!-- Login block screen (EDUS-2863) -->
  <section class="qa-section">
    <h2 class="heading-md">Pre-wizard — Login block screen</h2>
    <p class="subtitle">
      Force the login verdict for the selected test domain, so every block
      screen is reachable without arranging the state that normally produces it
      — no broken delegate, no detached domain, no second browser. Set it, then
      sign in as any user at that domain. Expires in {{ ttlLabel }} and reverts to a
      real resolution.
    </p>
    <p class="subtitle">
      <strong>Only block screens can be forced</strong> — app access and the
      onboarding wizard are deliberately not in this list, so this lever can
      never let anyone in. Two screens read licence facts this shortcut skips:
      <strong>No Active License</strong> always shows the upgrade copy (use the
      License section's <em>Lapsed</em> scenario for the lapsed design, which it
      reaches through the real path), and <strong>Configuration in Progress</strong>
      says "another administrator" instead of naming a colleague. Neither is a bug.
    </p>

    <div class="qa-form">
      <label class="qa-field">
        <span class="qa-label">Forced verdict</span>
        <select
          [(ngModel)]="blockVerdict"
          name="blockVerdict"
          data-testid="qa-verdict-select"
        >
          <option *ngFor="let v of blockVerdicts" [value]="v">
            {{ blockVerdictLabel[v] }} ({{ v }})
          </option>
        </select>
      </label>

      <div class="qa-actions">
        <lib-cdw-button
          level="primary"
          type="button"
          [disabled]="verdictBusy"
          (buttonClick)="applyVerdictOverride()"
          data-testid="qa-verdict-apply"
        >
          {{ verdictBusy ? 'Working…' : 'Set verdict override' }}
        </lib-cdw-button>
        <lib-cdw-button
          level="secondary"
          type="button"
          [disabled]="verdictBusy"
          (buttonClick)="clearVerdictOverride()"
          data-testid="qa-verdict-clear"
        >
          Clear verdict override
        </lib-cdw-button>
      </div>
    </div>

    <p *ngIf="verdictMessage" class="qa-message" data-testid="qa-verdict-message">
      {{ verdictMessage }}
    </p>
    <p *ngIf="verdictError" class="qa-error" data-testid="qa-verdict-error">
      {{ verdictError }}
    </p>
  </section>

  <!-- License override -->
  <section class="qa-section">
    <h2 class="heading-md">Pre-wizard — License</h2>
    <p class="subtitle">
      Seed a Chargify license scenario for the selected test domain, then sign
      in as a super admin there to see the resulting login verdict. Overrides
      expire in {{ ttlLabel }} and revert to the real Chargify state on the next login.
    </p>

    <div class="qa-form">
      <label class="qa-field">
        <span class="qa-label">Scenario</span>
        <select [(ngModel)]="scenario" name="scenario" data-testid="qa-scenario">
          <option *ngFor="let s of scenarios" [value]="s.key">
            {{ s.label }} ({{ s.key }})
          </option>
        </select>
      </label>

      <div class="qa-actions">
        <lib-cdw-button
          level="primary"
          type="button"
          [disabled]="licenseBusy"
          (buttonClick)="applyOverride()"
          data-testid="qa-apply"
        >
          {{ licenseBusy ? 'Working…' : 'Set override' }}
        </lib-cdw-button>
        <lib-cdw-button
          level="secondary"
          type="button"
          [disabled]="licenseBusy"
          (buttonClick)="clearOverride()"
          data-testid="qa-clear"
        >
          Clear override
        </lib-cdw-button>
      </div>
    </div>

    <p
      *ngIf="licenseMessage"
      class="qa-message"
      data-testid="qa-license-message"
    >
      {{ licenseMessage }}
    </p>
    <p *ngIf="licenseError" class="qa-error" data-testid="qa-license-error">
      {{ licenseError }}
    </p>
  </section>

  <!-- Wizard shell load error (EDUS-2855) -->
  <section class="qa-section">
    <h2 class="heading-md">Wizard shell — Load error</h2>
    <p class="subtitle">
      Force <code>GET /onboarding/progress</code> to fail. That call is what
      tells the shell which step to show, so failing it produces the whole-wizard
      load error rather than any one step's — the screen an admin gets when the
      wizard cannot work out where they are.
    </p>

    <ng-container
      *ngTemplateOutlet="
        failToggle;
        context: {
          endpoint: 'progress',
          where: 'Open the wizard as an SA at that domain — it will fail to load at all.'
        }
      "
    ></ng-container>
  </section>

  <!-- Step 1: Terms & Conditions -->
  <section class="qa-section">
    <h2 class="heading-md">Step 1 — Terms &amp; Conditions</h2>
    <p class="subtitle">
      Clear all recorded T&amp;C acceptances for the selected test customer so
      Wizard Step 1 can be replayed — including as the same user. Test customers
      only.
    </p>

    <div class="qa-actions">
      <lib-cdw-button
        level="secondary"
        type="button"
        [disabled]="termsBusy"
        (buttonClick)="clearTerms()"
        data-testid="qa-clear-terms"
      >
        {{ termsBusy ? 'Working…' : 'Clear T&C acceptances' }}
      </lib-cdw-button>
    </div>

    <p *ngIf="termsMessage" class="qa-message" data-testid="qa-terms-message">
      {{ termsMessage }}
    </p>
    <p *ngIf="termsError" class="qa-error" data-testid="qa-terms-error">
      {{ termsError }}
    </p>

    <p class="subtitle qa-subheading">
      Force <code>POST /onboarding/agreement/accept</code> to fail, for the error
      shown when accepting the terms does not go through. Clear the acceptances
      above first, or Step 1 will not be on screen to accept.
    </p>

    <ng-container
      *ngTemplateOutlet="
        failToggle;
        context: {
          endpoint: 'agreement-accept',
          where: 'Accept the terms on Step 1 as an SA at that domain.'
        }
      "
    ></ng-container>
  </section>

  <!-- Step 2 load error (EDUS-2855) -->
  <section class="qa-section">
    <h2 class="heading-md">Step 2 — Load error</h2>
    <p class="subtitle">
      Force <code>GET /onboarding/environment</code> to fail. That is the call
      behind Step 2's domain list and assigned service account, so failing it
      gives the step's load-error screen — distinct from the shell error above,
      which stops the wizard before any step renders.
    </p>

    <ng-container
      *ngTemplateOutlet="
        failToggle;
        context: {
          endpoint: 'environment',
          where: 'Open Step 2 as an SA at that domain.'
        }
      "
    ></ng-container>
  </section>

  <!-- Client-id load failure (Step-3 load error) -->
  <section class="qa-section">
    <h2 class="heading-md">Step 3 — Load error (client id)</h2>
    <p class="subtitle">
      Force the Step-3 client-id fetch (<code>GET /authentication/dwd-client-id</code>)
      to fail for the selected test customer, so you can reach the "we couldn't
      load your authorization details" screen — a distinct, earlier failure than
      the DWD verification outcomes below. Enable it, then open the wizard's
      Step 3 as an SA at that domain. Expires in {{ ttlLabel }} and reverts to a normal
      load.
    </p>

    <div class="qa-actions">
      <lib-cdw-button
        level="primary"
        type="button"
        [disabled]="clientIdFailBusy"
        (buttonClick)="setClientIdFail(true)"
        data-testid="qa-client-id-fail-enable"
      >
        {{ clientIdFailBusy ? 'Working…' : 'Force load error' }}
      </lib-cdw-button>
      <lib-cdw-button
        level="secondary"
        type="button"
        [disabled]="clientIdFailBusy"
        (buttonClick)="setClientIdFail(false)"
        data-testid="qa-client-id-fail-clear"
      >
        Clear
      </lib-cdw-button>
    </div>

    <p
      *ngIf="clientIdFailMessage"
      class="qa-message"
      data-testid="qa-client-id-fail-message"
    >
      {{ clientIdFailMessage }}
    </p>
    <p
      *ngIf="clientIdFailError"
      class="qa-error"
      data-testid="qa-client-id-fail-error"
    >
      {{ clientIdFailError }}
    </p>
  </section>

  <!-- Step 3 verify request failure (EDUS-2855) -->
  <section class="qa-section">
    <h2 class="heading-md">Step 3 — Verification request failure</h2>
    <p class="subtitle">
      Force <code>POST /onboarding/verify-delegation</code> to fail outright.
      <strong>Not the same as the DWD statuses below</strong>: those are
      verification running and coming back with a diagnosis, which produces a
      specific "here's what's wrong" card. This is the request never completing,
      which produces the plain "Verification Failed" fallback — the one with no
      diagnosis to offer.
    </p>

    <ng-container
      *ngTemplateOutlet="
        failToggle;
        context: {
          endpoint: 'verify-delegation',
          where: 'Run Step 3 → Verify as an SA at that domain.'
        }
      "
    ></ng-container>
  </section>

  <!-- DWD verify status -->
  <section class="qa-section">
    <h2 class="heading-md">Step 3 — DWD verify status</h2>
    <p class="subtitle">
      Force the wizard's Step-3 verification outcome for the selected test
      customer, so you can reach the failure views without a real broken DWD
      setup. After setting, open the wizard as a super admin at that domain and
      run Step 3 → Verify. Expires in {{ ttlLabel }} and reverts to the real check.
    </p>

    <div class="qa-form">
      <label class="qa-field">
        <span class="qa-label">Forced status</span>
        <select [(ngModel)]="dwdStatus" name="dwdStatus" data-testid="qa-dwd-status">
          <option *ngFor="let s of dwdStatuses" [value]="s">
            {{ dwdLabel(s) }} ({{ s }})
          </option>
        </select>
      </label>

      <div class="qa-actions">
        <lib-cdw-button
          level="primary"
          type="button"
          [disabled]="dwdBusy"
          (buttonClick)="applyDwdOverride()"
          data-testid="qa-dwd-apply"
        >
          {{ dwdBusy ? 'Working…' : 'Set DWD override' }}
        </lib-cdw-button>
        <lib-cdw-button
          level="secondary"
          type="button"
          [disabled]="dwdBusy"
          (buttonClick)="clearDwdOverride()"
          data-testid="qa-dwd-clear"
        >
          Clear DWD override
        </lib-cdw-button>
      </div>
    </div>

    <p *ngIf="dwdMessage" class="qa-message" data-testid="qa-dwd-message">
      {{ dwdMessage }}
    </p>
    <p *ngIf="dwdError" class="qa-error" data-testid="qa-dwd-error">
      {{ dwdError }}
    </p>
  </section>

  <!-- DWD propagation re-check (EDUS-2844) -->
  <section class="qa-section">
    <h2 class="heading-md">Step 3 — DWD propagation re-check</h2>
    <p class="subtitle">
      The Step-3 re-check runs hourly for 24 hours after a failed Verify. These
      two levers exist because the passage of time is the one thing the override
      above cannot fake. <strong>Start a re-check first</strong>: force a DWD
      failure above, then run Step 3 → Verify as a super admin at that domain.
    </p>
    <p class="subtitle">
      <strong>Backdate</strong> ages the clock — 24 or more puts the tenant
      straight onto the "we retried for 24 hours" screen and makes the next run
      send the stalled email. <strong>Run now</strong> fires one link of the
      chain immediately, through the same handler Cloud Tasks calls, so you can
      watch the background success path (and Step 3 moving on by itself) without
      waiting an hour.
    </p>

    <div class="qa-form">
      <label class="qa-field">
        <span class="qa-label">Backdate the clock by (hours)</span>
        <input
          type="number"
          min="1"
          max="48"
          [(ngModel)]="dwdRecheckHoursAgo"
          name="dwdRecheckHoursAgo"
          data-testid="qa-dwd-recheck-hours"
        />
      </label>

      <div class="qa-actions">
        <lib-cdw-button
          level="primary"
          type="button"
          [disabled]="dwdRecheckBusy"
          (buttonClick)="backdateDwdRecheck()"
          data-testid="qa-dwd-recheck-backdate"
        >
          {{ dwdRecheckBusy ? 'Working…' : 'Backdate re-check clock' }}
        </lib-cdw-button>
        <lib-cdw-button
          level="secondary"
          type="button"
          [disabled]="dwdRecheckBusy"
          (buttonClick)="runDwdRecheck()"
          data-testid="qa-dwd-recheck-run"
        >
          Run re-check now
        </lib-cdw-button>
      </div>
    </div>

    <p
      *ngIf="dwdRecheckMessage"
      class="qa-message"
      data-testid="qa-dwd-recheck-message"
    >
      {{ dwdRecheckMessage }}
    </p>
    <p
      *ngIf="dwdRecheckError"
      class="qa-error"
      data-testid="qa-dwd-recheck-error"
    >
      {{ dwdRecheckError }}
    </p>
  </section>

  <!-- Sync kickoff failure -->
  <section class="qa-section">
    <h2 class="heading-md">Step 4 — Sync kickoff failure</h2>
    <p class="subtitle">
      Force the Step-4 sync kickoff to fail for the selected test customer, so you
      can reach the "Sync Could Not Start" view without a real upstream failure.
      Enable it, then run Step 4 → "Sync Data" as an SA at that
      domain. Expires in {{ ttlLabel }} and reverts to a normal kickoff.
    </p>

    <div class="qa-actions">
      <lib-cdw-button
        level="primary"
        type="button"
        [disabled]="syncFailBusy"
        (buttonClick)="setSyncFail(true)"
        data-testid="qa-sync-fail-enable"
      >
        {{ syncFailBusy ? 'Working…' : 'Force sync failure' }}
      </lib-cdw-button>
      <lib-cdw-button
        level="secondary"
        type="button"
        [disabled]="syncFailBusy"
        (buttonClick)="setSyncFail(false)"
        data-testid="qa-sync-fail-clear"
      >
        Clear
      </lib-cdw-button>
    </div>

    <p *ngIf="syncFailMessage" class="qa-message" data-testid="qa-sync-fail-message">
      {{ syncFailMessage }}
    </p>
    <p *ngIf="syncFailError" class="qa-error" data-testid="qa-sync-fail-error">
      {{ syncFailError }}
    </p>
  </section>

  <!-- Next sync outcome (EDUS-2770). Deliberately below the kickoff lever:
       they are the two ends of the same step, in the order a sync meets them. -->
  <section class="qa-section">
    <h2 class="heading-md">Step 4 — Next sync outcome</h2>
    <p class="subtitle">
      Arm an outcome, then run Step 4 → "Sync Data" as an SA at
      that domain. The kickoff proceeds normally — which is what records the run
      and puts the person who clicked on the notification list — but no real sync
      is enqueued, and the armed outcome is reported straight away. So you get
      <strong>the email and the screen</strong>, in one click, with nothing
      running in the background to contradict it later.
    </p>
    <p class="subtitle">
      Unlike the lever above, which fails the kickoff REQUEST so no job is ever
      created. Arm <strong>success</strong> for the "Data Sync Ready" email and
      the <code>/tour</code> link with no waiting. Arm
      <strong>partial</strong> to check it is treated as a FAILURE here, which
      inverts the terminal-notification contract's general advice.
    </p>
    <p class="subtitle">
      Fires once per kickoff. The run it produces is real state, so clearing the
      lever afterwards does not rewind it — re-run the sync, or reset onboarding.
      This does not exercise Cloud Tasks or the OIDC hop; a real sync completing
      is what covers those.
    </p>

    <label class="qa-field">
      <span class="qa-label">Outcome</span>
      <select
        [(ngModel)]="syncOutcome"
        name="syncOutcome"
        data-testid="qa-sync-outcome-select"
      >
        <option *ngFor="let o of syncOutcomes" [value]="o">{{ o }}</option>
      </select>
    </label>

    <div class="qa-actions">
      <lib-cdw-button
        level="primary"
        type="button"
        [disabled]="syncOutcomeBusy"
        (buttonClick)="setSyncOutcome(syncOutcome)"
        data-testid="qa-sync-outcome-apply"
      >
        {{ syncOutcomeBusy ? 'Working…' : 'Arm sync outcome' }}
      </lib-cdw-button>
      <lib-cdw-button
        level="secondary"
        type="button"
        [disabled]="syncOutcomeBusy"
        (buttonClick)="setSyncOutcome(null)"
        data-testid="qa-sync-outcome-clear"
      >
        Clear
      </lib-cdw-button>
    </div>

    <p
      *ngIf="syncOutcomeMessage"
      class="qa-message"
      data-testid="qa-sync-outcome-message"
    >
      {{ syncOutcomeMessage }}
    </p>
    <p
      *ngIf="syncOutcomeError"
      class="qa-error"
      data-testid="qa-sync-outcome-error"
    >
      {{ syncOutcomeError }}
    </p>
  </section>

  <!-- Step 5 invite failures (EDUS-2855) -->
  <section class="qa-section">
    <h2 class="heading-md">Step 5 — Invite failures</h2>
    <p class="subtitle">
      Two separate screens, two separate levers.
      <strong>Address check</strong> fails
      <code>POST /onboarding/invite-users/validate</code>, which is what runs as
      you leave the field — you get the inline error under the input.
      <strong>Send</strong> fails <code>POST /onboarding/invite-users</code>,
      which gives the error banner on submit.
    </p>
    <p class="subtitle qa-caveat">
      <strong>Read the send lever carefully.</strong> It makes the banner render
      by failing the HTTP request, which is a <em>different code path</em> from a
      real send failure: Mandrill rejecting recipients still returns 200 with a
      failure list in the body, handled by the lever below. Seeing this banner
      work is not evidence that rejection handling works.
    </p>

    <p class="subtitle qa-subheading">Address check</p>
    <ng-container
      *ngTemplateOutlet="
        failToggle;
        context: {
          endpoint: 'invite-validate',
          where: 'Type an address on Step 5 and move focus out of the field.'
        }
      "
    ></ng-container>

    <p class="subtitle qa-subheading">Send — the request fails</p>
    <ng-container
      *ngTemplateOutlet="
        failToggle;
        context: {
          endpoint: 'invite-send',
          where: 'Submit the invitations on Step 5 as an SA at that domain.'
        }
      "
    ></ng-container>

    <p class="subtitle qa-subheading">Send — Mandrill rejects the recipients</p>
    <p class="subtitle">
      The other half of the send, and the one that matters. The toggle above
      fails the HTTP request; this makes the request <strong>succeed</strong>
      and come back with rejections in the body — which is how a real Mandrill
      rejection arrives, and the path where a total failure used to render
      "Users Successfully Invited". <strong>Nothing is emailed</strong> either
      way. Arm it, then submit on Step 5.
    </p>

    <div class="qa-form">
      <label class="qa-field">
        <span class="qa-label">Rejection</span>
        <select
          [(ngModel)]="inviteRejectMode"
          name="inviteRejectMode"
          data-testid="qa-invite-reject-select"
        >
          <option *ngFor="let m of inviteRejectModes" [value]="m">
            {{ inviteRejectLabel[m] }} ({{ m }})
          </option>
        </select>
      </label>

      <div class="qa-actions">
        <lib-cdw-button
          level="primary"
          type="button"
          [disabled]="inviteRejectBusy"
          (buttonClick)="setInviteReject(inviteRejectMode)"
          data-testid="qa-invite-reject-apply"
        >
          {{ inviteRejectBusy ? 'Working…' : 'Arm rejection' }}
        </lib-cdw-button>
        <lib-cdw-button
          level="secondary"
          type="button"
          [disabled]="inviteRejectBusy"
          (buttonClick)="setInviteReject(null)"
          data-testid="qa-invite-reject-clear"
        >
          Clear
        </lib-cdw-button>
      </div>
    </div>

    <p
      *ngIf="inviteRejectMessage"
      class="qa-message"
      data-testid="qa-invite-reject-message"
    >
      {{ inviteRejectMessage }}
    </p>
    <p
      *ngIf="inviteRejectError"
      class="qa-error"
      data-testid="qa-invite-reject-error"
    >
      {{ inviteRejectError }}
    </p>
  </section>

  <!-- Delete test customer (EDUS-3040). Last on the page, away from the
       everyday levers: unlike everything above it, it cannot be undone. -->
  <section class="qa-section" data-testid="qa-delete-customer-section">
    <h2 class="heading-md">Delete test customer</h2>
    <p class="subtitle">
      Permanently deletes the selected test customer — every SQL row, its
      Firestore job documents and Redis keys — so first-time onboarding
      (auto-create, the wizard from Step 1) can be re-tested from nothing. Its
      closed-beta allowlist entry is kept, so signing in again re-creates the
      tenant. Shows what will be deleted and asks
      you to type the domain before anything happens.
      <strong>It cannot be undone.</strong>
    </p>
    <p class="subtitle">
      Not the same as Reset onboarding on the customer page, which keeps the
      tenant and only rewinds its onboarding state. Test customers only — this is
      never how a real customer is removed.
    </p>
    <div class="qa-actions">
      <lib-cdw-button
        level="primary"
        type="button"
        [disabled]="deleteBusy || noTenantForDomain"
        (buttonClick)="confirmDeleteCustomer()"
        data-testid="qa-delete-customer"
      >
        {{ deleteBusy ? 'Working…' : 'Delete test customer' }}
      </lib-cdw-button>
      <span *ngIf="noTenantForDomain" class="subtitle" data-testid="qa-delete-customer-none">
        No tenant exists for {{ domain }}.
      </span>
    </div>
    <p *ngIf="deleteMessage" class="qa-message" data-testid="qa-delete-customer-message">
      {{ deleteMessage }}
    </p>
    <p *ngIf="deleteError" class="qa-error" data-testid="qa-delete-customer-error">
      {{ deleteError }}
    </p>
  </section>
</div>
